import React, { useState } from 'react';
import { Keyboard, View } from 'react-native';
import { IContextSheetContentProps, ISheetConfig } from '@/components/ui/ContextSheet';
import { VStack } from '@/components/gluestack/vstack';
import { Input, InputField } from '@/components/gluestack/input';
import { FormControl, FormControlError, FormControlErrorIcon, FormControlErrorText, FormControlHelper, FormControlHelperText, FormControlLabel, FormControlLabelText } from '@/components/gluestack/form-control';
import { Button, ButtonText } from '@/components/gluestack/button';
import { AlertCircleIcon } from '@/components/gluestack/icon';
import { z } from 'zod';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useToastMessages } from '@/hooks/useToastMessages';
import { AccountService } from '@/services/domain/AccountService';
import { useDelayedAutoFocus } from '@/hooks/useDelayedAutoFocus';
import { useAuth } from '@/components/providers/AuthProvider';

const usernameRegex = /^[A-Za-z][A-Za-z0-9_-]{1,19}$/;
type FormValues = { username: string };

export interface IUsernameChangeSheetProps extends IContextSheetContentProps {}

export default function UsernameChangeSheet({ onClose }: IUsernameChangeSheetProps) {
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast } = useToastMessages();
  const { signOutSoft, forget, currentCommunity, updateStoredUsername } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useDelayedAutoFocus();

  const schema = z.object({
    username: z
      .string()
      .min(2, { message: t('common.validation.min', { minLength: 2 }) })
      .max(20, { message: t('common.validation.max', { maxLength: 20 }) })
      .regex(usernameRegex, { message: t('settings.account.username.invalid') }),
  });

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { username: '' },
  });

  const onSubmit = async ({ username }: FormValues) => {
    Keyboard.dismiss();
    setSubmitting(true);
    try {
      try {
        await AccountService.isUserNameAvailable(username);
      } catch (e) {
        setError('username', { type: 'validate', message: t('settings.account.username.unavailable') });
        setSubmitting(false);
        return;
      }

      await AccountService.changeUserName(username);
      showSuccessToast(t('settings.account.username.successSignOut'));
      if (currentCommunity) {
        try {
          await updateStoredUsername(currentCommunity, username);
        } catch {}
      }
      await signOutSoft();
      // Updated stored credentials so that the new username is prefilled on sign-in
      onClose?.();
    } catch (error) {
      showErrorToast(t('settings.account.username.error'), error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <VStack className="flex-1 gap-4">
      <FormControl isInvalid={!!errors.username}>
        <FormControlLabel>
          <FormControlLabelText>{t('settings.account.username.label')}</FormControlLabelText>
        </FormControlLabel>
        <Controller
          control={control}
          name="username"
          render={({ field: { onChange, onBlur, value } }) => (
            <Input>
              <InputField
                ref={inputRef}
                placeholder={t('settings.account.username.placeholder')}
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                autoCapitalize="none"
                autoCorrect={false}
                returnKeyType="done"
                onSubmitEditing={handleSubmit(onSubmit)}
              />
            </Input>
          )}
        />
        <FormControlHelper>
          <FormControlHelperText>{t('settings.account.username.helper')}</FormControlHelperText>
        </FormControlHelper>
        <FormControlError>
          <FormControlErrorIcon as={AlertCircleIcon} />
          <FormControlErrorText>{errors.username?.message}</FormControlErrorText>
        </FormControlError>
      </FormControl>

      <View className="flex-1" />

      <Button onPress={handleSubmit(onSubmit)} isDisabled={submitting} className="mt-2 self-end">
        <ButtonText>{t('settings.account.username.updateAndSignOut')}</ButtonText>
      </Button>
    </VStack>
  );
}

UsernameChangeSheet.sheetConfig = {
  largeScreenMode: 'modal',
  size: 'sm',
  expectsKeyboard: true,
} satisfies ISheetConfig;


